// Docs: IMPLEMENTATION.md §3.2 (server state / freshness policy).
// The queries that render study progress: the Heute page (Inbox rows + plan
// progress line, NextUpCard, daily goal ring, pool panel) and the practice
// page's lesson cards. They must all be invalidated together whenever answers
// reach the backend, otherwise Heute keeps showing pre-practice counts.
import { queryKeys } from '../queryKeys';

export const PROGRESS_QUERY_KEYS = [
  queryKeys.inbox.all,
  queryKeys.lessons.active(),
  queryKeys.stats.summaryAll,
  // The points card moves with every answer, so it invalidates with the rest
  // of the progress surface rather than waiting out its staleTime.
  queryKeys.stats.points(),
  queryKeys.pool.status(),
];

/**
 * Invalidate every progress-facing query.
 *
 * Pass `{ refetchType: 'none' }` from hot paths (e.g. after each synced answer
 * batch inside a running drill): the queries are only marked stale, so nothing
 * refetches mid-lesson, but any later mount or window focus of Heute refetches
 * immediately — even inside the queries' staleTime window.
 */
export function invalidateProgressQueries(queryClient, options = {}) {
  for (const queryKey of PROGRESS_QUERY_KEYS) {
    queryClient.invalidateQueries({ queryKey, ...options });
  }
}

/**
 * Take a just-completed lesson out of the cached inbox.
 *
 * Finishing a lesson lands on Heute, which renders the cached inbox while the
 * invalidation refetch is in flight — without this, the hero card and ▶ keep
 * offering the finished lesson until the server answers.
 */
export function dropCompletedLessonFromInbox(queryClient, lessonId) {
  if (lessonId == null) return;
  queryClient.setQueryData(queryKeys.inbox.all, (inbox) => {
    if (!Array.isArray(inbox?.today)) return inbox;
    const today = inbox.today.filter(
      (item) => !(item.kind === 'lesson' && String(item.payload?.lesson_id) === String(lessonId)),
    );
    return today.length === inbox.today.length ? inbox : { ...inbox, today };
  });
}
